/**
 * Delivery-mode picker for Location Service Engine tenants (Instant / Express / Scheduled / Next-day), shown on the
 * cart and checkout screens of every theme. Renders nothing unless the server's ``/cart`` answer carries an
 * ``lse`` block — i.e. for every other tenant this component is invisible and the screens are unchanged.
 *
 * The options, dates, slots, fees and cut-off rules all come from the server (``lse.delivery.modes``); choosing
 * one re-prices the cart through the same endpoint, so the fee shown is the fee charged.
 */
import { AlertTriangle, CalendarDays, Clock, MapPin, Truck, Zap } from "lucide-react";
import { ZONE_TYPE_LABEL, inr } from "../../lib/lse";

const ICONS = { INSTANT: Zap, EXPRESS: Truck, SCHEDULED: CalendarDays, NEXT_DAY: Clock };
const ROLLED = {
    cut_off: "Today's cut-off has passed",
    capacity: "The earlier date is full",
    route_capacity: "Your village route is full on the earlier date",
};

export default function DeliveryOptions({ lse, value, onChange, compact = false }) {
    if (!lse) return null;
    const modes = lse.delivery?.modes || [];
    const selected = lse.delivery?.selected || null;
    const current = value?.mode ? value : (selected ? { mode: selected.mode, date: selected.date, slot_id: selected.slot?.id } : null);
    const zone = lse.zone;
    const issues = (lse.issues || []).filter((i) => i.code !== "CHOOSE_DELIVERY");
    if (!modes.length && !issues.length && !zone?.zone_type) return null;

    const pick = (mode, extra = {}) => onChange?.({ mode, ...extra });

    return (
        <section data-testid="lse-delivery" className={`bg-card border border-border rounded-2xl ${compact ? "p-3" : "p-4"}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="font-bold text-sm flex items-center gap-1.5"><Truck size={16} className="text-primary" /> Delivery</h3>
                {zone?.zone_type && (
                    <span data-testid="lse-zone-type" className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary inline-flex items-center gap-1">
                        <MapPin size={11} /> {ZONE_TYPE_LABEL[zone.zone_type] || zone.zone_type}{zone.name ? ` · ${zone.name}` : ""}
                    </span>
                )}
            </div>

            {issues.length > 0 && (
                <div data-testid="lse-issues" className="mt-3 space-y-2">
                    {issues.map((i) => (
                        <p key={i.code} className="text-xs rounded-xl bg-amber-50 text-amber-900 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-900 px-3 py-2 flex gap-2">
                            <AlertTriangle size={14} className="shrink-0 mt-0.5" /> <span>{i.message}</span>
                        </p>
                    ))}
                </div>
            )}

            {modes.length > 0 && (
                <div role="radiogroup" aria-label="Delivery option" className="mt-3 grid gap-2 sm:grid-cols-2">
                    {modes.map((m) => {
                        const Icon = ICONS[m.mode] || Truck;
                        const active = current?.mode === m.mode;
                        return (
                            <button
                                key={m.mode}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                disabled={!m.available}
                                data-testid={`lse-mode-${m.mode}`}
                                onClick={() => {
                                    if (m.mode === "SCHEDULED") {
                                        const d = (m.dates || []).find((x) => x.available);
                                        const s = d?.slots.find((x) => x.available);
                                        pick(m.mode, d && s ? { date: d.date, slot_id: s.id } : {});
                                    } else pick(m.mode, m.date && m.mode === "NEXT_DAY" ? { date: m.date } : {});
                                }}
                                className={`text-left rounded-xl border px-3 py-2.5 transition min-h-[56px] ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border"} ${m.available ? "" : "opacity-50 cursor-not-allowed"}`}
                            >
                                <span className="flex items-center justify-between gap-2">
                                    <span className="font-semibold text-sm inline-flex items-center gap-1.5"><Icon size={15} /> {m.label}</span>
                                    <span className="text-xs font-semibold text-muted-foreground">{Number(m.extra_fee) > 0 ? `+${inr(m.extra_fee)}` : "No extra fee"}</span>
                                </span>
                                <span className="block text-xs text-muted-foreground mt-0.5">
                                    {m.available ? (m.eta_text || m.date_label || "") : (m.reason || "Not available right now")}
                                </span>
                                {m.mode === "NEXT_DAY" && m.available && m.rolled_reason && (
                                    <span data-testid="lse-rolled" className="block text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">
                                        {ROLLED[m.rolled_reason] || "Next available date"} — {m.date_label}
                                    </span>
                                )}
                                {m.mode === "NEXT_DAY" && m.cut_off_time && (
                                    <span className="block text-[11px] text-muted-foreground">Order before {m.cut_off_time} for the next route</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {current?.mode === "SCHEDULED" && (() => {
                const m = modes.find((x) => x.mode === "SCHEDULED");
                if (!m) return null;
                const day = (m.dates || []).find((d) => d.date === current.date) || (m.dates || [])[0];
                return (
                    <div data-testid="lse-slots" className="mt-3">
                        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                            {(m.dates || []).map((d) => (
                                <button key={d.date} type="button" disabled={!d.available}
                                    data-testid={`lse-date-${d.date}`}
                                    onClick={() => { const s = d.slots.find((x) => x.available); pick("SCHEDULED", { date: d.date, slot_id: s?.id }); }}
                                    className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold border ${d.date === day?.date ? "bg-primary text-primary-foreground border-primary" : "border-border"} ${d.available ? "" : "opacity-40"}`}>
                                    {d.label}
                                </button>
                            ))}
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                            {(day?.slots || []).map((s) => (
                                <button key={s.id} type="button" disabled={!s.available}
                                    data-testid={`lse-slot-${s.id}`}
                                    onClick={() => pick("SCHEDULED", { date: day.date, slot_id: s.id })}
                                    className={`rounded-xl border px-2 py-2 text-xs font-semibold ${current.slot_id === s.id && current.date === day.date ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border"} ${s.available ? "" : "opacity-40 line-through"}`}>
                                    {s.label}
                                    {s.remaining != null && s.available && s.remaining <= 3 && (
                                        <span className="block text-[10px] font-medium text-amber-700 dark:text-amber-300">{s.remaining} left</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                );
            })()}
        </section>
    );
}
